import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import styled from "styled-components";
import * as localService from "../../services/localService";
import moneybag from "../../img/Web/Spacia/revenue.svg";
import booking_icon from "../../img/Web/Spacia/bookings.svg";
import home from "../../img/Web/Spacia/total-props.svg";
import published from "../../img/Web/Spacia/published-props.svg";
import moment from "moment-timezone";
import ResponsiveTable from "../../components/ResponsiveTable";
import RevenueReportModal from "../../components/RevenueReportModal";
import { TrendingUp } from "lucide-react";

import { useHistory } from "react-router-dom";
import { getCurrency } from "../../services/localService";
import logger from "../../utils/logger";
// import warnin from '../../assets/img/Web/Spacia/warning.png';
import warnin from "../../img/Web/Spacia/t-warning 3.svg";
import spaceManagementService from "../../services/host-apiServices/spaceManagementService";
import { getUserTimezone, getCurrentTimezoneOffset } from "../../utils/timezoneUtils";
import billingService from "../../services/billingService";

const shimmerKeyframes = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`;

const ShimmerStyles = styled.div`
  ${shimmerKeyframes}

  .shimmer {
    animation: shimmer 2s infinite linear;
    background: linear-gradient(to right, #f6f7f8 4%, #edeef1 25%, #f6f7f8 36%);
    background-size: 1000px 100%;
  }

  .shimmer-card {
    height: 100%;
    border-radius: 5px;
    padding: 1.75rem;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    border: 1px solid #f0f0f0;
  }

  .shimmer-content {
    display: flex;
    align-items: center;
  }

  .shimmer-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    margin-right: 1.25rem;
  }

  .shimmer-text {
    flex: 1;
  }

  .shimmer-title {
    height: 12px;
    width: 60%;
    margin-bottom: 0.75rem;
    border-radius: 4px;
  }

  .shimmer-value {
    height: 24px;
    width: 80%;
    border-radius: 4px;
  }
`;

const DashboardContainer = styled.div`
  height: calc(100vh - 80px);
  overflow-y: auto;
  padding: 24px;
  background: #f8f9fa;
`;

const StyledCard = styled(motion.div)`
  background: ${props => props.gradient || "white"};
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  padding: 1.25rem;
  height: 100%;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;

  ${props =>
    props.darkText &&
    `
        h1, h2, h3, h4, h5, h6 {
            color: #2c3e50;
        }
    `}

  ${props =>
    props.gradient &&
    `
        color: white;
        border: none;
    `}

    &:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  }

  h6 {
    color: ${props => (props.darkText ? "#2c3e50" : "#fff")};
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.9;
  }

  h1 {
    font-size: 2.2rem;
    font-weight: 700;
    margin-bottom: 0;
    color: ${props => (props.darkText ? "#2c3e50" : "#fff")};
  }

  img {
    width: 48px;
    height: 48px;
    margin-right: 1.25rem;
    filter: ${props => (props.darkText ? "none" : "brightness(0) invert(1)")};
    opacity: 0.9;
  }
`;

const DashboardHeader = styled.div`
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .greeting-section {
    h2 {
      color: #1a1a1a;
      font-size: 1.75rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    p {
      color: #6c757d;
      font-size: 1rem;
      margin: 0;
    }
  }

  .date-chip {
    background: rgba(0, 123, 255, 0.1);
    border: 1px solid rgba(0, 123, 255, 0.2);
    padding: 0.5rem 1rem;
    border-radius: 100px;
    color: #007bff;
    font-weight: 500;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(0, 123, 255, 0.15);
      transform: translateY(-1px);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const MetricCard = ({ icon, title, value, gradient, isLoading }) => {
  if (isLoading) {
    return (
      <div className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl animate-pulse"></div>
            <div className="space-y-2">
              <div className="h-4 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
              <div className="h-8 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
        {/* Shimmer effect */}
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
      </div>
    );
  }

  // Card color schemes based on gradient prop
  const cardSchemes = {
    "linear-gradient(45deg, #FF6B6B, #FF8E53)": {
      bg: "bg-gradient-to-br from-red-50 to-orange-50",
      iconBg: "bg-gradient-to-br from-red-500 to-orange-500",
      iconColor: "text-white",
      textColor: "text-gray-900",
      valueColor: "text-red-600",
      border: "border-red-100",
    },
    "linear-gradient(45deg, #4CAF50, #81C784)": {
      bg: "bg-gradient-to-br from-green-50 to-emerald-50",
      iconBg: "bg-gradient-to-br from-green-500 to-emerald-500",
      iconColor: "text-white",
      textColor: "text-gray-900",
      valueColor: "text-green-600",
      border: "border-green-100",
    },
    "linear-gradient(45deg, #2196F3, #64B5F6)": {
      bg: "bg-gradient-to-br from-blue-50 to-cyan-50",
      iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500",
      iconColor: "text-white",
      textColor: "text-gray-900",
      valueColor: "text-blue-600",
      border: "border-blue-100",
    },
    "linear-gradient(45deg, #9C27B0, #BA68C8)": {
      bg: "bg-gradient-to-br from-purple-50 to-violet-50",
      iconBg: "bg-gradient-to-br from-purple-500 to-violet-500",
      iconColor: "text-white",
      textColor: "text-gray-900",
      valueColor: "text-purple-600",
      border: "border-purple-100",
    },
  };

  const scheme = cardSchemes[gradient] || {
    bg: "bg-gradient-to-br from-gray-50 to-slate-50",
    iconBg: "bg-gradient-to-br from-gray-500 to-slate-500",
    iconColor: "text-white",
    textColor: "text-gray-900",
    valueColor: "text-gray-600",
    border: "border-gray-100",
  };

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl ${scheme.bg} p-6 shadow-sm border ${scheme.border} hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8">
          <div className={`w-full h-full rounded-full ${scheme.iconBg} blur-3xl`}></div>
        </div>
      </div>

      <div className="relative">
        {/* Top row: Icon */}
        <div className="flex items-start justify-between mb-3">
          <div
            className={`flex items-center justify-center w-12 h-12 rounded-xl ${scheme.iconBg} shadow-lg flex-shrink-0`}
          >
            <img src={icon} alt={title} className="w-7 h-7 object-contain filter brightness-0 invert" />
          </div>
        </div>

        {/* Bottom row: Title and Value */}
        <div className="space-y-1">
          <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">{title}</h3>
          <p className={`text-3xl font-bold ${scheme.valueColor}`}>{value}</p>
        </div>
      </div>

      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
    </div>
  );
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
};

const amountFormatter = (num, digits = 1) => {
  const currency = getCurrency() || "KES";
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" }, // lowercase k for thousand
    { value: 1e6, symbol: "m" }, // lowercase m for million
    { value: 1e9, symbol: "b" }, // b for billion instead of G
    { value: 1e12, symbol: "t" }, // t for trillion
    { value: 1e15, symbol: "q" }, // q for quadrillion
    { value: 1e18, symbol: "qt" }, // qt for quintillion
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return currency + " " + (item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0");
};

const formatDateTime = dateString => {
  const date = new Date(dateString);
  const now = new Date("2025-01-11T10:08:59Z"); // Using provided current time
  const diff = Math.floor((date - now) / (1000 * 60 * 60 * 24)); // difference in days

  // Format time consistently with user's timezone
  const timeStr = date.toLocaleTimeString("en-US", {
    timeZone: getUserTimezone(),
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Future dates
  if (diff > 0) {
    if (diff === 1) return `Tomorrow at ${timeStr}`;
    // All other future dates show regular format
    return (
      date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) + ` at ${timeStr}`
    );
  }

  // Past dates
  if (diff < 0) {
    if (diff === -1) return `Yesterday at ${timeStr}`;
    if (diff > -4) return `${Math.abs(diff)} days ago at ${timeStr}`;
    return (
      date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }) + ` at ${timeStr}`
    );
  }

  // Today
  return `Today at ${timeStr}`;
};

const Dashboard = () => {
  const [totalPropertiesCount, setTotalPropertiesCount] = useState(0);
  const [totalBookingsToday, setTotalBookingsToday] = useState(0);
  const [totalBookingCancellationWithin7Days, setTotalBookingCancellationWithin7Days] = useState(0);
  const [publishedPropertiesTotal, setPublishedPropertiesTotal] = useState(0);
  const [unpublishedPropertiesTotal, setUnpublishedPropertiesTotal] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  const [inactiveUsersCount, setInactiveUsersCount] = useState(0);
  const [bookings, setBookings] = useState(0);
  const [pendingPropertiesLength, setPendingPropertiesLength] = useState(0);
  const [bookingsReceived, setBookingsReceived] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0.0);

  const [publishedProperties, setPublishedProperties] = useState([]);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [showFinishSetupModal, setShowFinishSetupModal] = useState(false);
  const [chartData, setChartData] = useState([]);

  const [userRole, setUserRole] = useState(null);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  const history = useHistory();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const [loading, setLoading] = useState({
    allData: true,
    properties: true,
    bookings: true,
    bookingsReceived: true,
    revenue: true,
    chart: true,
  });

  const [currentTime, setCurrentTime] = useState(moment());
  const currentUser = localService.getUser();
  const userName = currentUser?.firstName || "User";

  const [showDocumentWarning, setShowDocumentWarning] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(moment());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const currentUser = localService.getUser();
    const userId = currentUser ? currentUser.id : 0;
    logger.log("Admin Dashboard useEffect - User ID:", userId);
    setUserRole("ROLE_ADMINISTRATOR");

    // Initialize all values to 0 for smooth loading
    setTotalPropertiesCount(0);
    setPublishedPropertiesTotal(0);
    setUnpublishedPropertiesTotal(0);
    setBookings(0);
    setTotalRevenue(0);

    // Track all API calls
    let completedCalls = 0;
    const totalExpectedCalls = 4; // spaceStatistics, publishedSpaces, bookingStatistics, recentProperties

    const markCallComplete = () => {
      completedCalls++;
      if (completedCalls === totalExpectedCalls) {
        setLoading(prev => ({ ...prev, allData: false }));
      }
    };

    // Fetch recent properties using spaceManagementService (same as properties screen)
    setLoadingProperties(true);

    spaceManagementService
      .getSpaces({
        page: 0,
        size: 5,
        sortBy: "createdOn",
        sortOrder: "desc",
      })
      .then(res => {
        logger.log("Recent properties API response:", res);
        logger.log("Recent properties data:", res.data);
        logger.log("Recent properties data.data:", res.data.data);
        logger.log("Recent properties data.content:", res.data.content);

        if (res.status === 200) {
          const properties = res.data.content || [];
          logger.log("Setting recent properties:", properties);
          logger.log("Properties length:", properties.length);
          setRecentProperties(properties);
        } else {
          logger.log("API response status not 200:", res.status);
          setRecentProperties([]);
        }
      })
      .catch(error => {
        logger.error("Error fetching recent properties:", error);
        // Don't show toast for recent properties fetch failure
        setRecentProperties([]);
      })
      .finally(() => {
        setLoadingProperties(false);
        markCallComplete();
      });

    // Get space statistics from space management service
    logger.log("Admin user detected, using space management service");

    spaceManagementService
      .getSpaceStatistics()
      .then(res => {
        logger.log("Space statistics response:", res);
        if (res.status === 200) {
          logger.log("Space statistics from space management service:", res.data);
          setTotalPropertiesCount(res.data.totalSpaces || 0);
          // Note: availableSpaces means "not currently booked", not "published"
          // Published count is fetched separately via searchSpaces with status: "PUBLISHED"
          setUnpublishedPropertiesTotal(res.data.bookedSpaces || 0);

          // Log the values being set
          logger.log("Setting totalPropertiesCount:", res.data.totalSpaces || 0);
        }
      })
      .catch(error => {
        logger.error("Error fetching space statistics:", error);
        // Set default values instead of showing error toast
        setTotalPropertiesCount(0);
        setUnpublishedPropertiesTotal(0);
      })
      .finally(() => {
        setLoading(prev => ({ ...prev, properties: false }));
        markCallComplete();
      });

    // Get published properties count using search endpoint
    // No size parameter - we only need totalElements count from the response
    spaceManagementService
      .searchSpaces({ status: "PUBLISHED" })
      .then(res => {
        logger.log("Published spaces search response:", res);
        if (res.status === 200) {
          // Get count from totalElements in pagination part
          const totalCount = res.data.totalElements || 0;
          logger.log("Published spaces count from totalElements:", totalCount);
          setPublishedPropertiesTotal(totalCount);
        }
      })
      .catch(error => {
        logger.error("Error fetching published spaces:", error);
        // Set default value instead of showing error toast
        setPublishedPropertiesTotal(0);
      })
      .finally(() => {
        setLoading(prev => ({ ...prev, publishedProperties: false }));
        markCallComplete();
      });

    // Get booking statistics from space management service
    logger.log("Fetching admin-specific metrics using space management service");

    // Get booking statistics from space management service
    spaceManagementService
      .getBookingStatistics()
      .then(res => {
        logger.log("Booking statistics response:", res);
        if (res.status === 200) {
          logger.log("Booking statistics from space management service:", res.data);
          setBookings(res.data.totalBookings || 0);
          logger.log("Setting total bookings:", res.data.totalBookings || 0);
        }
      })
      .catch(error => {
        logger.error("Error fetching booking statistics:", error);
        // Set default value instead of showing error toast
        setBookings(0);
      })
      .finally(() => {
        setLoading(prev => ({ ...prev, bookings: false }));
        markCallComplete();
      });

    // Get platform-wide revenue statistics (for admin dashboard)
    const fetchRevenue = async () => {
      try {
        setLoading(prev => ({ ...prev, revenue: true }));
        const startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
        const endDate = moment().format("YYYY-MM-DD");

        // Use platform revenue report for admin dashboard
        const res = await billingService.getPlatformRevenueReport({
          startDate,
          endDate,
        });

        if (res.status === 200) {
          // Calculate total revenue from platform revenue share
          // The platform revenue share represents the platform's cut
          const platformRevenue = res.data?.data?.revenueShare?.platform || [];
          const totalRevenueAmount = platformRevenue.reduce((acc, curr) => {
            return acc + parseFloat(curr.amount || 0);
          }, 0);
          setTotalRevenue(totalRevenueAmount);
          logger.log("Platform revenue:", totalRevenueAmount);
        }
      } catch (error) {
        logger.error("Error fetching platform revenue:", error);
      } finally {
        setLoading(prev => ({ ...prev, revenue: false }));
        markCallComplete();
      }
    };

    fetchRevenue();
  }, []);

  useEffect(() => {
    const currentUser = localService.getUser();
    const userRole = currentUser?.role;

    // Only check for HOST roles
    if (userRole === "ROLE_HOST" || userRole === "ROLE_HOST_OPERATIONS") {
      const userId = currentUser?.id;
      const companyId = currentUser?.companyId;

      // Company documents check removed for admin-only dashboard
      // Document warning functionality can be added back if needed for admin users
    }
  }, []);

  // Revenue Card Component - Clickable to show revenue report modal
  const RevenueCard = ({ isLoading }) => {
    if (isLoading) {
      return (
        <div className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-4 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
                <div className="h-8 w-16 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        </div>
      );
    }

    return (
      <div
        onClick={() => setShowRevenueModal(true)}
        className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-50 to-cyan-50 p-6 shadow-sm border border-blue-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8">
            <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 blur-3xl"></div>
          </div>
        </div>

        <div className="relative">
          {/* Top row: Icon and View Details */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg flex-shrink-0">
              <img src={moneybag} alt="Revenue" className="w-7 h-7 object-contain filter brightness-0 invert" />
            </div>

            <div className="flex items-center gap-1 text-blue-500 opacity-60 group-hover:opacity-100 transition-opacity">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-medium">View More</span>
            </div>
          </div>

          {/* Bottom row: Title and Value */}
          <div className="space-y-1">
            <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Platform Commission Revenue</h3>
            <p className="text-3xl font-bold text-blue-600">{amountFormatter(totalRevenue)}</p>
          </div>
        </div>

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      </div>
    );
  };

  const renderMetrics = () => {
    const metrics = [];

    logger.log("renderMetrics called - userRole:", userRole);
    logger.log("Current state values:");
    logger.log("- totalPropertiesCount:", totalPropertiesCount);
    logger.log("- publishedPropertiesTotal:", publishedPropertiesTotal);
    logger.log("- totalRevenue:", totalRevenue);
    logger.log("- bookings:", bookings);
    logger.log("- loading state:", loading);

    // Admin-only metrics - show all cards at once with unified loading
    metrics.push(
      <MetricCard
        key="total-properties"
        icon={home}
        title="Total Spaces & Services"
        value={loading.allData ? "0" : totalPropertiesCount}
        gradient="linear-gradient(45deg, #FF6B6B, #FF8E53)"
        isLoading={loading.allData}
      />,
      <MetricCard
        key="published-properties"
        icon={published}
        title="Published Spaces & Services"
        value={loading.allData ? "0" : publishedPropertiesTotal}
        gradient="linear-gradient(45deg, #4CAF50, #81C784)"
        isLoading={loading.allData}
      />,
      <RevenueCard key="revenue" isLoading={loading.allData} />,
      <MetricCard
        key="total-bookings"
        icon={booking_icon}
        title="Total Bookings"
        value={loading.allData ? "0" : bookings}
        gradient="linear-gradient(45deg, #9C27B0, #BA68C8)"
        isLoading={loading.allData}
      />
    );

    return metrics;
  };

  const handleStuffOnModalHide = () => {
    setShowFinishSetupModal(false);

    const dashboardContainer = document.querySelector(".dashboard-container");
    if (dashboardContainer != null) {
      dashboardContainer.classList.remove("blur-background");
    }
  };

  return (
    <div className="min-h-screen">
      <div className="h-[calc(100vh-80px)] overflow-y-auto p-0 sm:p-8">
        {/* Shimmer effect container */}
        <div>
          <div className="px-2 mx-auto w-full max-w-7xl sm:px-4">
            {/* Dashboard Header */}
            <div className="flex flex-col justify-between items-start px-3 pt-4 mb-8 sm:flex-row sm:items-center sm:mb-10 sm:px-0 sm:pt-0">
              <div className="mb-6 greeting-section sm:mb-0">
                <h2 className="flex gap-3 items-center mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
                  <span className="text-4xl">👋</span>
                  {getGreeting()}, {userName}
                </h2>
                <p className="text-base text-gray-600 sm:text-lg">Here's what's happening with your properties today</p>
              </div>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 py-3 px-4 rounded-2xl text-blue-700 font-semibold text-sm flex items-center gap-3 hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md">
                <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <span className="font-medium">{currentTime.format("MMMM D, YYYY")}</span>
              </div>
            </div>

            {/* Main Content */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              {/* Metrics Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 px-1 mb-8 sm:px-0 sm:mb-10">
                {renderMetrics()}
              </div>

              {/* Recent Properties Section - Admin Only */}
              <div className="px-2 mb-6 sm:mb-8 sm:px-0">
                {/* {logger.log("Rendering ResponsiveTable with data:", recentProperties, "loading:", loadingProperties)} */}
                <ResponsiveTable
                  title="Recent Spaces & Services"
                  data={recentProperties}
                  loading={loadingProperties}
                  emptyMessage="Get started by creating your first property"
                  viewAllLink={
                    userRole !== "ROLE_SUBSCRIBER_OWNER"
                      ? {
                          to: "/spaces-and-services",
                          label: "View All Spaces & Services",
                        }
                      : null
                  }
                  statusIndicator={property => {
                    // Handle V2Space structure - status might be in instances[0] or at root
                    let status;
                    if (property.instances && property.instances.length > 0) {
                      status =
                        property.instances[0].status ||
                        property.instances[0].propertyStatus ||
                        property.instances[0].status?.value;
                    }
                    // Fallback to old structure
                    if (!status) {
                      status = property.status || property.propertyStatus || property.status?.value;
                    }
                    if (!status) return null;

                    const statusColors = {
                      published: "#4CAF50", // Green
                      unpublished: "#757575", // Gray
                    };

                    const color = statusColors[status.toLowerCase()] || "#757575";

                    return <div className="mr-2 w-2 h-2 rounded-full" style={{ backgroundColor: color }} />;
                  }}
                  columns={[
                    {
                      key: "title",
                      header: "Listing",
                      render: item => {
                        logger.log("Rendering property item:", item);
                        // Handle V2Space structure - title is in instances[0]
                        if (item.instances && item.instances.length > 0) {
                          return item.instances[0].title || item.instances[0].name || "Unnamed Listing";
                        }
                        // Fallback to old structure
                        return item.title || item.name || "Unnamed Listing";
                      },
                    },
                    {
                      key: "location",
                      header: "Location",
                      mobileLabel: "Location",
                      render: item => {
                        // Handle V2Space structure
                        if (item.instances && item.instances.length > 0) {
                          const location = item.instances[0].location?.buildingAddress;
                          if (location) {
                            const parts = [];
                            if (location.city) parts.push(location.city);
                            if (location.region) parts.push(location.region);
                            if (location.country) parts.push(location.country);
                            return parts.length > 0 ? parts.join(", ") : "N/A";
                          }
                        }

                        // Fallback to old structure
                        const location = item.location;
                        if (!location) return "N/A";

                        const parts = [];
                        if (location.city) parts.push(location.city);
                        if (location.region) parts.push(location.region);
                        if (location.country) parts.push(location.country);

                        return parts.length > 0 ? parts.join(", ") : "N/A";
                      },
                    },
                    {
                      key: "ratePrice",
                      header: "Price",
                      mobileLabel: "Price",
                      render: item => {
                        const price = item.ratePrice || item.propertyPrice?.price || item.price;
                        if (!price) return "N/A";
                        return amountFormatter(price, 1);
                      },
                    },
                    {
                      key: "status",
                      header: "Status",
                      mobileLabel: "Status",
                      render: item => {
                        // Handle V2Space structure - status might be in instances[0] or at root
                        let status;
                        if (item.instances && item.instances.length > 0) {
                          status =
                            item.instances[0].status ||
                            item.instances[0].propertyStatus ||
                            item.instances[0].status?.value;
                        }
                        // Fallback to old structure
                        if (!status) {
                          status = item.status || item.propertyStatus || item.status?.value;
                        }
                        if (!status) return "N/A";

                        const statusMap = {
                          published: "Published",
                          unpublished: "Unpublished",
                          private: "Private",
                          draft: "Draft",
                          pending: "Pending",
                          active: "Active",
                          inactive: "Inactive",
                        };

                        return statusMap[status.toLowerCase()] || status;
                      },
                    },
                    {
                      key: "createdAt",
                      header: "Added On",
                      mobileLabel: "Added",
                      render: item => {
                        // Handle V2Space structure - createdAt is in instances[0]
                        let date;
                        if (item.instances && item.instances.length > 0) {
                          date =
                            item.instances[0].createdAt || item.instances[0].createdDate || item.instances[0].createdOn;
                        }
                        // Fallback to old structure
                        if (!date) {
                          date = item.createdAt || item.createdDate || item.createdOn;
                        }
                        if (!date) return "N/A";
                        return formatDateTime(date);
                      },
                    },
                  ]}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Document Warning Modal */}
      {showDocumentWarning && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="p-4 w-full max-w-md bg-white rounded-lg shadow-xl sm:p-6">
            <div className="flex items-center mb-4">
              <img src={warnin} alt="Warning" className="mr-3 w-6 h-6 sm:w-8 sm:h-8" />
              <h3 className="text-lg font-medium text-red-600 sm:text-xl">Important Notice</h3>
            </div>
            <p className="mb-4 text-sm text-gray-700 sm:text-base">
              Your company profile is incomplete. Please upload the required documents to complete your profile setup.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDocumentWarning(false)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md text-sm"
              >
                Later
              </button>
              <button
                onClick={() => {
                  setShowDocumentWarning(false);
                  history.push("/settings");
                }}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
              >
                Go to Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revenue Report Modal */}
      <RevenueReportModal isOpen={showRevenueModal} onClose={() => setShowRevenueModal(false)} />
    </div>
  );
};

export default Dashboard;
